import { Children } from 'react'
import { ChevronRight } from 'lucide-react'
import { useControllableValue } from '@/ui/kit'
import { cn } from './internal/utils'
import { BORDER, RADIUS } from './internal/presets'

const Panel = ({ children }) => children

/** Accepts the v5 `items` array or legacy `Collapse.Panel` children. */
const normalizeItems = (items, children) => {
  if (items?.length) return items
  return Children.toArray(children)
    .filter(Boolean)
    .map((child, index) => ({
      key: child.props.panelKey ?? child.key?.replace(/^\.\$/, '') ?? String(index),
      label: child.props.header,
      children: child.props.children,
      extra: child.props.extra,
      collapsible: child.props.collapsible,
      showArrow: child.props.showArrow,
    }))
}

const toArray = (value) => {
  if (value == null) return []
  return Array.isArray(value) ? value.map(String) : [String(value)]
}

/**
 * antd Collapse. Supports `accordion`, `activeKey`/`defaultActiveKey` (string,
 * number or array), `ghost`, `bordered`, `expandIconPosition` and `destroyInactivePanel`.
 */
const Collapse = ({
  items,
  children,
  activeKey,
  defaultActiveKey,
  onChange,
  accordion = false,
  bordered = true,
  ghost = false,
  size = 'middle',
  expandIcon,
  expandIconPosition = 'start',
  destroyInactivePanel = false,
  className,
  style,
  ...rest
}) => {
  const resolved = normalizeItems(items, children)
  const [active, setActive] = useControllableValue(
    activeKey !== undefined ? toArray(activeKey) : undefined,
    toArray(defaultActiveKey)
  )

  const toggle = (key) => {
    const stringKey = String(key)
    const isOpen = active.includes(stringKey)
    const next = accordion
      ? isOpen
        ? []
        : [stringKey]
      : isOpen
        ? active.filter((item) => item !== stringKey)
        : [...active, stringKey]
    setActive(next)
    // antd hands back a string in accordion mode and an array otherwise.
    onChange?.(accordion ? next[0] : next)
  }

  const isEnd = expandIconPosition === 'end'

  return (
    <div
      className={cn(
        'ui-collapse',
        ghost ? 'ui-collapse-ghost' : cn('bg-[var(--surface-card)]', bordered && `border ${BORDER} ${RADIUS}`),
        'overflow-hidden',
        className
      )}
      style={style}
      {...rest}
    >
      {resolved.map((item) => {
        const stringKey = String(item.key)
        const isOpen = active.includes(stringKey)
        const isDisabled = item.collapsible === 'disabled'

        const arrow =
          item.showArrow === false ? null : (
            <span className={cn('ui-collapse-arrow shrink-0 transition-transform', isOpen && 'rotate-90')}>
              {expandIcon ? expandIcon({ isActive: isOpen }) : <ChevronRight size={14} aria-hidden="true" />}
            </span>
          )

        return (
          <div key={stringKey} className={cn('ui-collapse-item', !ghost && `border-b last:border-b-0 ${BORDER}`)}>
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={`collapse-panel-${stringKey}`}
              disabled={isDisabled}
              onClick={() => !isDisabled && toggle(item.key)}
              className={cn(
                'ui-collapse-header flex w-full items-center gap-2 bg-[var(--gray-100)] text-left font-medium',
                size === 'small'
                  ? 'min-h-[var(--control-height-sm)] px-2 text-sm'
                  : 'min-h-[var(--control-height)] px-3 text-sm',
                ghost && 'bg-transparent',
                isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-[var(--gray-300)]',
                isEnd && 'flex-row-reverse'
              )}
            >
              {arrow}
              <span className="ui-collapse-header-text min-w-0 flex-1 truncate">{item.label}</span>
              {item.extra ? (
                <span
                  className="ui-collapse-extra shrink-0"
                  role="presentation"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {item.extra}
                </span>
              ) : null}
            </button>
            {destroyInactivePanel && !isOpen ? null : (
              <div
                id={`collapse-panel-${stringKey}`}
                hidden={!isOpen}
                className={cn('ui-collapse-content bg-[var(--surface-card)]', isOpen && 'ui-collapse-content-active')}
              >
                <div className={cn('ui-collapse-content-box', size === 'small' ? 'p-2' : 'p-3')}>
                  {item.children}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

Collapse.Panel = Panel

export default Collapse
